import { Link } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────
// Sections optionnelles de la page d'accueil publique :
//   • CtaSection — bloc « Appel à l'action » (bouton vers un formulaire,
//     un contact, une réservation ou un lien externe).
//   • BookingCalendarSection — bloc « Calendrier de réservation » qui
//     met en avant le calendrier par défaut choisi par le marchand.
// Ces sections sont purement présentationnelles : la page d'accueil
// décide de leur affichage (bloc activé ou non) et de leur position.
// ─────────────────────────────────────────────────────────────

function isExternal(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

export function CtaSection({
  title,
  text,
  buttonLabel,
  url,
}: {
  title: string;
  text: string;
  buttonLabel: string;
  url: string;
}) {
  const hasContent = Boolean(title.trim() || text.trim());
  if (!hasContent) return null;

  const label = buttonLabel.trim() || 'En savoir plus';
  const href = url.trim();
  const buttonClass =
    'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap no-underline hover:bg-primary-600 transition-colors cursor-pointer';

  return (
    <section className="py-10 md:py-16">
      <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
        <div className="relative overflow-hidden rounded-2xl border border-background-200/70 bg-background-100/60 px-6 py-10 md:px-12 md:py-14 text-center">
          <div className="absolute -top-16 -right-10 w-48 h-48 rounded-full bg-accent-100/60 blur-2xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -left-12 w-56 h-56 rounded-full bg-primary-100/50 blur-2xl pointer-events-none"></div>

          <div className="relative">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-[11px] font-semibold uppercase tracking-wider mb-4">
              <i className="ri-megaphone-line"></i>
              À ne pas manquer
            </span>
            {title.trim() && (
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3 leading-tight">
                {title.trim()}
              </h2>
            )}
            {text.trim() && (
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mx-auto whitespace-pre-line mb-6">
                {text.trim()}
              </p>
            )}

            {href && (isExternal(href) ? (
              <a href={href} target="_blank" rel="noopener noreferrer" className={buttonClass}>
                <i className="ri-arrow-right-up-line"></i>
                {label}
              </a>
            ) : (
              <Link to={href} className={buttonClass}>
                <i className="ri-arrow-right-line"></i>
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export function BookingCalendarSection({
  serviceId,
  serviceName,
}: {
  serviceId: number | null;
  serviceName?: string | null;
}) {
  const href = serviceId ? `/booking?service=${serviceId}` : '/booking';

  return (
    <section className="py-10 md:py-16 bg-background-100/50">
      <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
        <div className="flex flex-col lg:flex-row items-stretch gap-0 rounded-2xl overflow-hidden border border-background-200/70 bg-background-50">
          <div className="flex-1 p-6 md:p-8">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-[11px] font-semibold uppercase tracking-wider mb-3">
              <i className="ri-calendar-check-line"></i>
              Réservation en ligne
            </span>
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
              Réservez votre créneau
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-5 max-w-md">
              Consultez les disponibilités en temps réel et choisissez le moment qui vous convient.
              {serviceName ? (
                <> Prestation mise en avant : <strong className="text-foreground-800">{serviceName}</strong>.</>
              ) : null}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to={href}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold no-underline whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-calendar-line"></i>
                Voir le calendrier
              </Link>
              <span className="text-xs text-foreground-500 flex items-center gap-1.5">
                <i className="ri-shield-check-line text-accent-600"></i>
                Confirmation immédiate
              </span>
            </div>
          </div>

          <div className="lg:w-72 bg-background-100/70 border-t lg:border-t-0 lg:border-l border-background-200/70 p-6 flex flex-col justify-center gap-3">
            <div className="grid grid-cols-7 gap-1.5">
              {Array.from({ length: 21 }).map((_, i) => {
                const active = [3, 5, 9, 12, 16, 18].includes(i);
                return (
                  <span
                    key={i}
                    className={`aspect-square rounded-md flex items-center justify-center text-[10px] font-medium ${
                      active
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-400 border border-background-200/60'
                    }`}
                  >
                    {i + 1}
                  </span>
                );
              })}
            </div>
            <p className="text-[11px] text-foreground-500 flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              Créneaux disponibles cette semaine
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}